import { Button, FormHelperText, Grid, TextField } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";
import { useLazyGetRegionListQuery } from "store/services/LocationsApi/regionApi";
import RichTextEditor from "components/Shared/RichTextEditor";

const DistrictForm = ({
  districtDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      name: districtDetail?.name ?? "",
      code: districtDetail?.code ?? "",
      coordinates: districtDetail?.coordinates || { lat: "", long: "" },
      description: districtDetail?.description ?? "",
      region: districtDetail?.region
        ? {
            label: districtDetail?.region?.name,
            id: districtDetail?.region?._id,
          }
        : "",
      status: districtDetail?.status === "Inactive" ? false : true,
    },
  });

  // RTK Query
  const [
    fetchregion,
    { data: regionRes, isLoading: loadingRegion, isFetching: fetchingRegion },
  ] = useLazyGetRegionListQuery();

  const region = React.useMemo(() => regionRes?.data ?? [], [regionRes?.data]);

  const regionOption = React.useMemo(() => {
    return region.map((region) => ({
      label: region.name,
      id: region._id,
    }));
  }, [region]);

  const handlePayload = (data) => {
    const _payload = {
      ...data,
      region: data.region.id,
    };

    onSubmit(_payload);
  };

  React.useEffect(() => {
    fetchregion({ status: "Active" });
  }, [fetchregion]);

  React.useEffect(() => {
    if (districtDetail) {
      reset({
        name: districtDetail.name,
        code: districtDetail.code,
        coordinates: districtDetail.coordinates || { lat: "", long: "" },
        description: districtDetail.description,
        region: districtDetail.region
          ? {
              label: districtDetail.region.name,
              id: districtDetail.region._id,
            }
          : "",
        status: districtDetail.status === "Inactive" ? false : true,
      });
    }
  }, [districtDetail, reset]);

  const renderTextField = (name, label, type = "text", rules = {}) => (
    <Grid item md={6}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <TextField
            fullWidth
            label={label}
            type={type}
            size="small"
            {...field}
          />
        )}
      />
    </Grid>
  );

  return (
    <AppForm loading={loadingRegion || fetchingRegion}>
      <AppForm.Content>
        <Grid container spacing={3} paddingY={2}>
          <Grid item md={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Name"
                  type="text"
                  size="small"
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="code"
              control={control}
              rules={{
                required: "Code is required",
                maxLength: {
                  value: 8,
                  message: "Code must be at most 8 characters long",
                },
                validate: (value) => {
                  if (value !== value.toUpperCase()) {
                    return "Code must be in uppercase";
                  }
                  if (!/^[A-Z0-9]+$/.test(value)) {
                    return "Code can only contain uppercase letters and numbers";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Code"
                  type="text"
                  size="small"
                  error={!!errors?.code}
                  helperText={errors?.code?.message}
                />
              )}
            />
          </Grid>
          {renderTextField("coordinates.lat", "Latitude", "number", {})}
          {renderTextField("coordinates.long", "Longitude", "number", {})}

          <Grid item xs={6}>
            <Controller
              name="region"
              control={control}
              rules={{ required: "Region is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Region"
                  loading={loadingRegion || fetchingRegion}
                  loadingText="Loading region..."
                  options={regionOption ?? []}
                  value={field?.value ?? ""}
                  onChange={(_, data) => {
                    setValue("region", data);
                  }}
                  error={!!errors?.region}
                  helperText={errors?.region?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <AppSwitch {...field} label="Is Active?" />
              )}
            />
          </Grid>

          <Grid item md={12}>
            <FormHelperText>Description</FormHelperText>

            <Controller
              name="description"
              control={control}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    districtDetail?.description
                      ? districtDetail?.description
                      : "<p>Enter Note Content.</p>"
                  }
                  onChange={onChange}
                  height={200}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handlePayload)}
        >
          {districtDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default DistrictForm;
